import { useState } from 'react'
import { X } from 'lucide-react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from '@/components/ui/drawer'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
interface ReportGenerationDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onGenerateReport: (reportData: ReportFormData) => void
  isLoading?: boolean
}

export interface ReportFormData {
  reportType: string
  dateRange: string
  sponsor: string
  placementLocation: string
}

export function ReportGenerationDrawer({
  open,
  onOpenChange,
  onGenerateReport,
  isLoading = false,
}: Readonly<ReportGenerationDrawerProps>) {
  const [formData, setFormData] = useState<ReportFormData>({
    reportType: '',
    dateRange: '',
    sponsor: '',
    placementLocation: '',
  })

  const handleFieldChange = (field: keyof ReportFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleCancel = () => {
    // Reset form data
    setFormData({
      reportType: '',
      dateRange: '',
      sponsor: '',
      placementLocation: '',
    })
    onOpenChange(false)
  }

  const handleGenerate = () => {
    onGenerateReport(formData)
  }

  const isFormValid =
    formData.reportType &&
    formData.dateRange &&
    formData.sponsor &&
    formData.placementLocation

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      direction="right"
      aria-label="Generate Ad Performance Report drawer"
    >
      <DrawerContent className="w-full sm:w-[400px] h-full bg-ss-neutral-white border-l border-ss-neutral-50 rounded-none md:rounded-xl">
        {/* Header */}
        <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-ss-neutral-50">
          <DrawerTitle
            className="text-lg font-semibold text-ss-neutral-950 leading-7"
            id="report-generation-drawer-title"
          >
            Generate Ad Performance Report
          </DrawerTitle>
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-ss-action-gray-50"
              aria-label="Close report generation drawer"
            >
              <X className="h-5 w-5 text-ss-neutral-600" aria-hidden="true" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        {/* Form Fields */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Report Type */}
          <div className="space-y-1">
            <Label
              className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
              htmlFor="report-type-select"
            >
              Report Type
            </Label>
            <Select
              value={formData.reportType}
              onValueChange={value => handleFieldChange('reportType', value)}
            >
              <SelectTrigger
                id="date-range-select"
                className={cn(
                  'w-full h-11 border border-ss-neutral-100 bg-ss-neutral-white',
                  'hover:border-ss-blue-700 focus:border-ss-blue-700',
                  'rounded-lg px-3 py-0'
                )}
                aria-label="Select a report type"
              >
                <SelectValue
                  placeholder="Select date range"
                  className="text-base font-normal text-ss-neutral-400 leading-[22px]"
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="performance">Impressions Summary</SelectItem>
                <SelectItem value="demographics">Clicks Report</SelectItem>
                <SelectItem value="revenue">CTR (%) Report</SelectItem>
                <SelectItem value="engagement">
                  Interactions Analysis
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date Range */}
          <div className="space-y-1">
            <Label
              className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
              htmlFor="date-range-container"
            >
              Date Range
            </Label>
            <Select
              value={formData.dateRange}
              onValueChange={value => handleFieldChange('dateRange', value)}
            >
              <SelectTrigger
                id="report-type-select"
                className={cn(
                  'w-full h-11 border border-ss-neutral-100 bg-ss-neutral-white',
                  'hover:border-ss-blue-700 focus:border-ss-blue-700',
                  'rounded-lg px-3 py-0'
                )}
                aria-label="Select date range"
              >
                <SelectValue
                  placeholder="Select a report type"
                  className="text-base font-normal text-ss-neutral-400 leading-[22px]"
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="performance">Last 24 Hours</SelectItem>
                <SelectItem value="demographics">Last 7 Days</SelectItem>
                <SelectItem value="revenue">Last 30 Days</SelectItem>
                <SelectItem value="engagement">Last 90 Days</SelectItem>
                <SelectItem value="alltime">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sponsor */}
          <div className="space-y-1">
            <Label
              className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
              htmlFor="sponsor-select"
            >
              Sponsor
            </Label>
            <Select
              value={formData.sponsor}
              onValueChange={value => handleFieldChange('sponsor', value)}
            >
              <SelectTrigger
                id="sponsor-select"
                className={cn(
                  'w-full h-11 border border-ss-neutral-100 bg-ss-neutral-white',
                  'hover:border-ss-blue-700 focus:border-ss-blue-700',
                  'rounded-lg px-3 py-0'
                )}
                aria-label="Select sponsor"
              >
                <SelectValue
                  placeholder="Select sponsor"
                  className="text-base font-normal text-ss-neutral-400 leading-[22px]"
                />
              </SelectTrigger>
              <SelectContent className="h-60 overflow-auto">
                <SelectItem value="alice-johnson">Alice Johnson</SelectItem>
                <SelectItem value="brian-smith">Brian Smith</SelectItem>
                <SelectItem value="carla-lee">Carla Lee</SelectItem>
                <SelectItem value="david-kim">David Kim</SelectItem>
                <SelectItem value="emily-chen">Emily Chen</SelectItem>
                <SelectItem value="marvin-mckinney">Marvin McKinney</SelectItem>
                <SelectItem value="theresa-webb">Theresa Webb</SelectItem>
                <SelectItem value="leslie-alexander">
                  Leslie Alexander
                </SelectItem>
                <SelectItem value="albert-flores">Albert Flores</SelectItem>
                <SelectItem value="jacob-jones">Jacob Jones</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Placement Location */}
          <div className="space-y-1">
            <Label
              className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
              htmlFor="placement-location-select"
            >
              Placement Location
            </Label>
            <Select
              value={formData.placementLocation}
              onValueChange={value =>
                handleFieldChange('placementLocation', value)
              }
            >
              <SelectTrigger
                id="placement-location-select"
                className={cn(
                  'w-full h-11 border border-ss-neutral-100 bg-ss-neutral-white',
                  'hover:border-ss-blue-700 focus:border-ss-blue-700',
                  'rounded-lg px-3 py-0'
                )}
                aria-label="Select placement location"
              >
                <SelectValue
                  placeholder="Select placement location"
                  className="text-base font-normal text-ss-neutral-400 leading-[22px]"
                />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-auto">
                <SelectItem value="hero-sponsor">Hero sponsor</SelectItem>
                <SelectItem value="in-feed-native">
                  In-feed native card
                </SelectItem>
                <SelectItem value="right-rail">Right-rail display</SelectItem>
                <SelectItem value="mobile-footer">
                  Mobile sticky footer
                </SelectItem>
                <SelectItem value="event-detail">Event detail</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Footer with buttons */}
        <DrawerFooter className="p-6 border-t border-ss-neutral-50 bg-ss-neutral-white shadow-[0px_5px_40px_0px_rgba(0,0,0,0.1)] rounded-bl-xl rounded-br-xl">
          <div className="flex items-center justify-between w-full">
            <Button
              variant="outline"
              onClick={handleCancel}
              className={cn(
                'h-9 px-4 border border-ss-neutral-200 text-ss-neutral-950',
                'hover:bg-ss-action-gray-50 font-semibold text-sm leading-4.5 ',
                'rounded-lg'
              )}
              aria-label="Cancel report generation"
              tabIndex={0}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={!isFormValid || isLoading}
              className={cn('h-9 ')}
              aria-label="Generate and download report"
              tabIndex={0}
            >
              {isLoading ? 'Generating...' : 'Generate & Download Report'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
